import React from "react"
import {useNavigate} from "react-router-dom"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import ShowerOutlinedIcon from "@mui/icons-material/ShowerOutlined"
import BedOutlinedIcon from "@mui/icons-material/BedOutlined"
import ApartmentIcon from "@mui/icons-material/Apartment"
import TimeToLeaveIcon from "@mui/icons-material/TimeToLeave"
import SquareFoot from "@mui/icons-material/SquareFoot"
import GroupIcon from "@mui/icons-material/Group"
import StatusTile from "./StatusTile"
import {PropertyType, regionTextByCode} from "@casas-diamante/types"
import {PropertyResponse as Property} from "@/types/Property"
import ShareBtn from "./ShareBtn"
import OpportunityTagBadge from "./OpportunityTagBadge"
import {useAuthContext} from "@/hooks/useAuthContext"
import {getImageURL} from "@/utils"
import {persistPropertySearchState, PropertySearchState} from "@/hooks/useBackNavigation"

function InmuebleCard({
   property,
   searchState,
}: {
   property: Property
   searchState?: PropertySearchState
}) {
   const navigate = useNavigate()
   const {user} = useAuthContext()

   const toProperty = () => {
      if (searchState?.source === "properties") {
         persistPropertySearchState({
            ...searchState,
            scrollY: window.scrollY,
            selectedPropertyId: property.id,
         })
      }

      // navigate to another screen passing the inmueble and img condominio
      navigate(`/inmuebles/${property.id}`, {
         state: {
            property,
            fromSearch: searchState
               ? {
                    ...searchState,
                    scrollY: window.scrollY,
                    selectedPropertyId: property.id,
                 }
               : undefined,
         },
      })
   }

   const SchemeElement = (scheme = "sale") => (
      <p className="px-4 border border-blue rounded-md text-blue shadow-md">
         {scheme === "sale" ? "Venta" : "Renta"}
      </p>
   )

   const IconInfo = ({
      icon,
      text,
      mts = false,
   }: {
      icon: React.ReactNode
      text: string | number
      mts?: boolean
   }) => (
      <li className="self-center flex items-center space-x-1">
         <span className="text-green">{icon}</span>
         <p>
            {text || "-"}
            {mts && <sup>2</sup>}
         </p>
      </li>
   )

   return (
      <div
         data-property-id={property.id}
         className="flex min-h-[690px] w-[320px] flex-col items-center gap-2 rounded-lg border p-2 shadow-md hover:shadow-lg relative cursor-pointer"
         onClick={toProperty}
      >
         <div className="relative w-full">
            <img
               src={getImageURL(property.mainImage)}
               className="rounded-lg w-full object-cover h-[250px]"
            />
            <OpportunityTagBadge
               tag={property.opportunityTag}
               className="absolute bottom-2 left-0 z-10 pointer-events-none h-[26px]"
            />
         </div>
         <ShareBtn
            className="absolute top-6 right-6 h-[40px] w-[40px] flex items-center justify-center"
            region={property.region}
            name={property.name}
            condoId={property.condo || ""}
            costs={{
               ...property.costos,
               salePrice: property.costoVenta,
               maintenance: property.maintenance,
               pricePerMeter: property.pricePerMeter,
            }}
            slug={property.slug}
            description={property.desc2 || ""}
            type={property.type}
            explicitRent={false}
            zone={property.zone || ""}
            capacity={property.capacity || 0}
            surface={property.mts || 0}
            constructionMts={property.mtsConstruction || 0}
            mapsShareUrl={property.mapsShareUrl || ""}
            videos={property.videos || []}
            accessToBeach={property.accessToBeach || ""}
         />
         <section className="flex gap-4">
            {property.forSale && SchemeElement()}
            {property.forRent && SchemeElement("rent")}
         </section>
         <StatusTile status={property.status} className="self-end" />
         <header className="min-w-0 w-full text-start">
            <h3 className="break-words whitespace-normal text-lg font-bold">{property.name}</h3>
            <h4 className="text-green">{regionTextByCode[property.region]}</h4>
            <p className="break-words whitespace-normal text-sm">
               {property.condoName ? property.condoName : "Sin condominio"}
               {property.condoClass ? ` - ${property.condoClass}` : ""}
            </p>
            <p className="text-sm">{property.type}</p>
            <p className="text-sm">{property.accessToBeach}</p>
            {user.admin && (
               <p className="w-full break-words whitespace-normal text-start text-sm font-bold text-green">
                  {property.partnerNames
                     ? `Responsable: ${property.partnerNames.map((name: string) => name)}`
                     : "Sin responsable"}
               </p>
            )}
         </header>
         <ul className="flex flex-wrap text-sm space-x-2 text-start w-full">
            <IconInfo icon={<SquareFoot />} text={property.mts ? property.mts + " mts" : "-"} mts />
            <IconInfo
               icon={
                  property.type === PropertyType.House ? <HomeOutlinedIcon /> : <ApartmentIcon />
               }
               text={property.mtsConstruction ? property.mtsConstruction + " mts" : "-"}
               mts
            />
            <IconInfo icon={<ShowerOutlinedIcon />} text={property.bathrooms ?? "-"} />
            <IconInfo icon={<BedOutlinedIcon />} text={property.rooms ?? "-"} />
            <IconInfo icon={<TimeToLeaveIcon />} text={property.parkingLots ?? "-"} />
            <IconInfo icon={<GroupIcon />} text={property.capacity ?? "-"} />
         </ul>
         <section className="flex w-full flex-col items-start gap-0 text-start text-sm">
            <p className="break-words">{`Precio de venta: ${
               property.costoVenta ? property.costoVenta.toLocaleString() + " MXN" : "-"
            }`}</p>
            <p className="break-words">
               {`
                  Renta/noche temporada baja:
                  ${
                     property.costos?.noche_regular
                        ? property.costos.noche_regular.toLocaleString() + " MXN"
                        : "-"
                  }`}
            </p>
            <p className="break-words">
               {`
                  Renta/noche temporada media:
                  ${
                     property.costos?.noche_media
                        ? property.costos.noche_media.toLocaleString() + " MXN"
                        : "-"
                  }`}
            </p>
            <p className="break-words">
               {`
                  Renta/noche temporada alta:
                  ${
                     property.costos?.noche_alta
                        ? property.costos.noche_alta.toLocaleString() + " MXN"
                        : "-"
                  }`}
            </p>
            <p className="break-words">
               {`
                  Renta/mes contrato regular: ${
                     property.costos?.mensual_regular
                        ? property.costos.mensual_regular.toLocaleString() + " MXN"
                        : "-"
                  }`}
            </p>
            <p className="break-words">
               {`Renta/mes contrato anual: ${
                  property.costos?.mensual_anual
                     ? property.costos.mensual_anual.toLocaleString() + " MXN"
                     : "-"
               }`}
            </p>
         </section>
         {/* tags for available, suspended and updated */}
         <section className="flex items-center justify-center gap-2">
            <div
               className={`border rounded inline-block ${
                  property.suspended ? "bg-red" : "bg-green"
               } text-white font-bold text-sm  p-1 text-center`}
            >
               {property.suspended ? "Apagado" : "Encendido"}
            </div>
            <div
               className={`border rounded inline-block ${
                  property.available ? "bg-green" : "bg-red"
               } text-white font-bold text-sm  p-1 text-center`}
            >
               {property.available ? "Disponible" : "No disponible"}
            </div>
         </section>
      </div>
   )
}

export default InmuebleCard
